import React, { useMemo, useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { useAuth0 } from '@auth0/auth0-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faThLarge, faFolderOpen, faUsers, faUtensils } from '@fortawesome/free-solid-svg-icons';
import AddAreaModal from '../modals/AddAreaModal';
import EditAreaModal from '../modals/EditAreaModal';
import AreaDetailsModal from '../modals/AreaDetailsModal';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';
import AdminPageHeader from '../shared/AdminPageHeader';
import AdminNotice from '../shared/AdminNotice';
import AdminEmptyState from '../shared/AdminEmptyState';
import AdminSectionCard from '../shared/AdminSectionCard';
import LoadingSpinner from '../LoadingSpinner';
import '../../styles/route_pages/Areas.css';
import { addArea, deleteArea, updateArea } from '../../utils/areaService';
import { useVenueId } from '../../hooks/useUserData';
import { errorTranslations } from '../../utils/errorMessages';

const Areas = () => {
  const venueId = useVenueId();
  const { areas, users, items, setAreas } = useAdmin();
  const { getAccessTokenSilently } = useAuth0();
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [currentArea, setCurrentArea] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState(null);

  const handleAreaAdded = async (newArea) => {
    try {
      setLoading(true);
      const addedArea = await addArea(venueId, { ...newArea, venueId: parseInt(venueId, 10) }, getAccessTokenSilently);
      setAreas([...areas, addedArea]);
      setIsAddModalVisible(false);
    } catch (error) {
      console.error('Failed to add area:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAreaUpdated = async (updatedArea) => {
    try {
      setLoading(true);
      const updated = await updateArea(venueId, updatedArea, getAccessTokenSilently);
      setAreas(areas.map((area) => (area.id === updated.id ? updated : area)));
      setIsEditModalVisible(false);
      setErrorMessage('');
    } catch (error) {
      console.error('Failed to update area:', error);
      setErrorMessage(errorTranslations[error.message] || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!areaToDelete) {
      return;
    }

    try {
      setLoading(true);
      await deleteArea(venueId, areaToDelete.id, getAccessTokenSilently);
      setAreas(areas.filter((area) => area.id !== areaToDelete.id));
      setShowDeleteConfirmation(false);
      setAreaToDelete(null);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(errorTranslations[error.message] || error.message);
    } finally {
      setLoading(false);
    }
  };

  const getDeleteConsequences = (area) => {
    const areaUsers = users.filter((user) => user.areaId === area.id);
    const areaItems = items.filter((item) => item.areaId === area.id);
    const consequences = [];

    if (areaUsers.length > 0) {
      consequences.push(`${areaUsers.length} kullanıcının alan ataması kaldırılacak.`);
    }

    if (areaItems.length > 0) {
      consequences.push(`${areaItems.length} ürünün alan ataması kaldırılacak.`);
    }

    if (!consequences.length) {
      consequences.push('Alan tamamen silinecek.');
    }

    return consequences;
  };

  const areaSummaries = useMemo(
    () =>
      areas.map((area) => ({
        ...area,
        usersCount: users.filter((entry) => entry.areaId === area.id).length,
        itemsCount: items.filter((entry) => entry.areaId === area.id).length,
      })),
    [areas, items, users],
  );

  return (
    <div className="page-container">
      <AdminPageHeader
        title="Alanlar"
        subtitle="Servis alanlarını, ekip kapsamasını ve ürün görünürlüğünü daha okunur bir yönetim listesiyle yönetin."
        actions={(
          <button className="btn btn-primary" onClick={() => setIsAddModalVisible(true)}>
            <FontAwesomeIcon icon={faPlus} />
            Yeni Alan
          </button>
        )}
      />

      {errorMessage ? (
        <AdminNotice tone="error" title="Alan işlemi tamamlanamadı" message={errorMessage} onDismiss={() => setErrorMessage('')} />
      ) : null}

      <div className="admin-page-stack">
        <AdminSectionCard>
          {loading ? <LoadingSpinner /> : null}
          {areaSummaries.length === 0 ? (
            <AdminEmptyState
              icon={faFolderOpen}
              title="Henüz alan bulunmuyor"
              description="Çalışma düzeninizi bölmek için ilk alanı oluşturarak başlayın."
              action={(
                <button className="btn btn-primary" onClick={() => setIsAddModalVisible(true)}>
                  <FontAwesomeIcon icon={faPlus} />
                  İlk Alanı Ekle
                </button>
              )}
            />
          ) : (
            <div className="admin-management-list areas-management-list">
              {areaSummaries.map((area) => (
                <article key={area.id} className="admin-management-row areas-row">
                  <div className="admin-management-leading">
                    <div className="admin-symbol-token areas-token">
                      <FontAwesomeIcon icon={faThLarge} />
                    </div>
                    <div className="admin-management-main">
                      <h3 className="admin-management-title">{area.name}</h3>
                      <div className="admin-chip-row">
                        <span className="admin-chip admin-chip-accent">
                          <FontAwesomeIcon icon={faUsers} />
                          {area.usersCount} kullanıcı
                        </span>
                        <span className="admin-chip admin-chip-success">
                          <FontAwesomeIcon icon={faUtensils} />
                          {area.itemsCount} ürün
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="admin-management-meta">
                    <div className="admin-stat-stack">
                      <div className="admin-stat-card">
                        <strong>{area.usersCount}</strong>
                        <span>Alan kullanıcısı</span>
                      </div>
                      <div className="admin-stat-card">
                        <strong>{area.itemsCount}</strong>
                        <span>Alan ürünü</span>
                      </div>
                    </div>
                  </div>

                  <div className="admin-management-actions">
                    <div className="admin-action-cluster">
                      <button type="button" className="btn btn-secondary" onClick={() => { setCurrentArea(area); setIsDetailsModalVisible(true); }}>
                        Detay
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => { setCurrentArea(area); setIsEditModalVisible(true); }}>
                        <FontAwesomeIcon icon={faEdit} />
                        Düzenle
                      </button>
                      <button type="button" className="btn btn-danger" onClick={() => { setAreaToDelete(area); setShowDeleteConfirmation(true); }}>
                        <FontAwesomeIcon icon={faTrash} />
                        Sil
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </AdminSectionCard>
      </div>

      <AddAreaModal show={isAddModalVisible} onClose={() => setIsAddModalVisible(false)} onAreaAdded={handleAreaAdded} />
      {currentArea ? (
        <>
          <EditAreaModal
            show={isEditModalVisible}
            onClose={() => setIsEditModalVisible(false)}
            area={currentArea}
            onAreaUpdated={handleAreaUpdated}
          />
          <AreaDetailsModal
            show={isDetailsModalVisible}
            onClose={() => setIsDetailsModalVisible(false)}
            area={currentArea}
            users={users.filter((user) => user.areaId === currentArea.id)}
            items={items.filter((item) => item.areaId === currentArea.id)}
          />
        </>
      ) : null}

      <DeleteConfirmationModal
        show={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        title="Alanı Sil"
        itemName={areaToDelete?.name}
        message={`"${areaToDelete?.name}" alanını silmek istediğinizden emin misiniz?`}
        consequences={areaToDelete ? getDeleteConsequences(areaToDelete) : []}
        loading={loading}
        type="danger"
      />
    </div>
  );
};

export default Areas;
